import React, { useState, useEffect } from "react";
import {
    Users, Search, Download, MoreHorizontal, Mail, Activity,
    TrendingUp, AlertCircle, Zap, LayoutDashboard, X, Check,
    Globe, Building2, Settings, ShieldCheck, ToggleLeft, ToggleRight,
    Cpu, Command, ChevronDown, ChevronUp, CreditCard, FileText, CheckCircle
} from "lucide-react";
import {
    collection, query, where, orderBy, getDocs, addDoc,
    doc, updateDoc, serverTimestamp
} from "firebase/firestore";
import { db } from "../../firebase";
import { useNavigate } from "react-router-dom";
import useCurrentUser from "../../hooks/useCurrentUser";
import api from "../../utils/api";

const PLAN_OPTIONS = [
    { value: "starter",      label: "Starter",      mrr: 99,  priceRange: "$99–149/mo" },
    { value: "growth",       label: "Growth",       mrr: 249, priceRange: "$249–399/mo" },
    { value: "professional", label: "Professional", mrr: 599, priceRange: "$599+/mo" },
];

const PLAN_FEATURES = {
    starter:      ["shipments", "inventory", "support", "chat", "customs"],
    growth:       ["shipments", "inventory", "support", "chat", "customs", "wms", "analytics", "documents", "hscode"],
    professional: ["shipments", "inventory", "support", "chat", "customs", "wms", "analytics", "documents", "hscode", "tms"],
};

export const PLATFORM_MODULES = [
    { id: "shipments", label: "Shipments", group: "Operations" },
    { id: "customs", label: "Customs Entries", group: "Operations" },
    { id: "inventory", label: "Inventory (3PL)", group: "Operations" },
    { id: "wms", label: "Warehouse Map", group: "Operations" },
    { id: "tms", label: "TMS / Logistics", group: "Operations" },
    { id: "analytics", label: "Analytics & Reports", group: "Data" },
    { id: "support", label: "Support Tickets", group: "Communication" },
    { id: "chat", label: "Chat / Messaging", group: "Communication" },
    { id: "hscode", label: "HS Code Database", group: "Compliance" },
    { id: "documents", label: "Document Storage", group: "Data" },
];

const groupedModules = PLATFORM_MODULES.reduce((acc, mod) => {
    if (!acc[mod.group]) acc[mod.group] = [];
    acc[mod.group].push(mod);
    return acc;
}, {});

export default function TenantManagement() {
    const navigate = useNavigate();
    const { role } = useCurrentUser();
    const baseRoute = role === "super-admin" ? "/super-admin" : "/admin";

    const [filter, setFilter] = useState("all");
    const [searchTerm, setSearchTerm] = useState("");
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [userCounts, setUserCounts] = useState({});
    const [stats, setStats] = useState({ totalTenants: 0, totalMrr: 0, atRisk: 0 });
    const [expandedRow, setExpandedRow] = useState(null);

    // Provision modal
    const [showProvision, setShowProvision] = useState(false);
    const [provisioning, setProvisioning] = useState(false);
    const [provisionForm, setProvisionForm] = useState({ name: "", contactEmail: "", country: "AU", plan: "starter" });
    const [provisionError, setProvisionError] = useState("");

    // Square invoice
    const [invoicingSending, setInvoicingSending] = useState(null);
    const [invoiceFlash, setInvoiceFlash] = useState({});

    const sendPlanInvoice = async (tenantId, tenantName) => {
        setInvoicingSending(tenantId);
        try {
            const res = await api.post("/api/payments/plan-invoice", { tenantId });
            setInvoiceFlash(f => ({ ...f, [tenantId]: { ok: true, msg: `Invoice sent! Payment link: ${res.data.paymentLink}` } }));
            setTimeout(() => setInvoiceFlash(f => { const n = { ...f }; delete n[tenantId]; return n; }), 8000);
        } catch (e) {
            setInvoiceFlash(f => ({ ...f, [tenantId]: { ok: false, msg: e.response?.data?.error || "Failed to send invoice." } }));
            setTimeout(() => setInvoiceFlash(f => { const n = { ...f }; delete n[tenantId]; return n; }), 8000);
        } finally {
            setInvoicingSending(null);
        }
    };

    // Feature edit modal
    const [editingTenant, setEditingTenant] = useState(null);
    const [tempFeatures, setTempFeatures] = useState([]);
    const [savingFeatures, setSavingFeatures] = useState(false);

    useEffect(() => { fetchTenants(); }, [filter]);

    const fetchTenants = async () => {
        setLoading(true);
        try {
            const companiesRef = collection(db, "companies");
            let q = filter === "all"
                ? query(companiesRef, orderBy("createdAt", "desc"))
                : query(companiesRef, where("type", "==", filter));

            const snapshot = await getDocs(q);
            const fetched = [];
            let totalMrr = 0;
            let atRisk = 0;

            snapshot.forEach((docSnap) => {
                const data = docSnap.data();
                if (data.status === "deleted") return;
                const mrr = data.mrr || 0;
                const healthScore = data.healthScore || 100;
                totalMrr += mrr;
                if (healthScore < 50 || data.status === "suspended" || data.status === "overdue") atRisk++;
                fetched.push({ id: docSnap.id, ...data, mrr, healthScore });
            });

            if (filter !== "all") {
                fetched.sort((a, b) => {
                    const ms = (ts) => ts?.toDate ? ts.toDate().getTime() : 0;
                    return ms(b.createdAt) - ms(a.createdAt);
                });
            }

            setTenants(fetched);
            setStats({ totalTenants: fetched.length, totalMrr, atRisk });

            const usersSnap = await getDocs(collection(db, "users"));
            const counts = {};
            usersSnap.forEach((u) => {
                const cid = u.data().companyId;
                if (cid) counts[cid] = (counts[cid] || 0) + 1;
            });
            setUserCounts(counts);
        } catch (err) {
            console.error("Error fetching tenants:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleProvision = async (e) => {
        e.preventDefault();
        setProvisionError("");
        if (!provisionForm.name.trim()) return setProvisionError("Company name is required.");
        if (!provisionForm.contactEmail.trim()) return setProvisionError("Contact email is required.");
        setProvisioning(true);
        try {
            const plan = provisionForm.plan;
            const planData = PLAN_OPTIONS.find(p => p.value === plan);
            await addDoc(collection(db, "companies"), {
                name: provisionForm.name.trim(),
                contactEmail: provisionForm.contactEmail.trim().toLowerCase(),
                country: provisionForm.country.trim() || "AU",
                type: plan === "professional" ? "enterprise" : plan === "growth" ? "sme" : "starter",
                paymentPlan: plan,
                status: "active",
                createdAt: serverTimestamp(),
                enabledFeatures: PLAN_FEATURES[plan] || [],
                mrr: planData?.mrr || 0,
                healthScore: 100,
            });
            setShowProvision(false);
            setProvisionForm({ name: "", contactEmail: "", country: "AU", plan: "starter" });
            fetchTenants();
        } catch (err) {
            console.error("Provision error:", err);
            setProvisionError("Failed to create tenant. Please try again.");
        } finally {
            setProvisioning(false);
        }
    };

    const openFeatureEditor = (tenant) => {
        setEditingTenant(tenant);
        setTempFeatures([...(tenant.enabledFeatures || [])]);
    };

    const toggleFeature = (id) => {
        setTempFeatures(prev => prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]);
    };

    const saveFeatures = async () => {
        if (!editingTenant) return;
        setSavingFeatures(true);
        try {
            await updateDoc(doc(db, "companies", editingTenant.id), { enabledFeatures: tempFeatures });
            setTenants(prev => prev.map(t => t.id === editingTenant.id ? { ...t, enabledFeatures: tempFeatures } : t));
            setEditingTenant(null);
        } catch (err) {
            console.error("Feature save error:", err);
            alert("Failed to update features.");
        } finally {
            setSavingFeatures(false);
        }
    };

    const displayed = tenants.filter((t) => {
        if (!searchTerm) return true;
        const s = searchTerm.toLowerCase();
        return (
            t.name?.toLowerCase().includes(s) ||
            t.id?.toLowerCase().includes(s) ||
            t.contactEmail?.toLowerCase().includes(s) ||
            t.abn?.toLowerCase().includes(s)
        );
    });

    const getStatusColor = (status) => {
        const s = status?.toLowerCase() || "";
        if (s === "active") return "bg-emerald-100 text-emerald-700";
        if (s === "overdue") return "bg-amber-100 text-amber-700";
        if (s === "suspended") return "bg-red-100 text-red-700";
        return "bg-slate-100 text-slate-700";
    };

    const formatDate = (ts) => {
        if (!ts) return "—";
        const d = ts.toDate ? ts.toDate() : new Date(ts);
        return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    };

    const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : "";

    return (
        <div className="max-w-7xl mx-auto py-8">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-navy-900 font-serif flex items-center gap-3">
                        <Command size={28} className="text-gold-500" /> Tenant Management
                    </h1>
                    <p className="text-slate-500 mt-1">Manage all registered tenants — directory, features, and provisioning in one place.</p>
                </div>
                <div className="flex gap-3">
                    <button className="px-4 py-2 bg-white border border-slate-300 text-navy-900 rounded-lg hover:bg-slate-50 font-bold flex items-center gap-2 transition-all shadow-sm">
                        <Download size={18} /> Export
                    </button>
                    <button
                        onClick={() => setShowProvision(true)}
                        className="px-4 py-2 bg-navy-900 text-white rounded-lg hover:bg-navy-800 font-bold flex items-center gap-2 transition-all shadow-lg shadow-navy-900/20"
                    >
                        <Zap size={18} /> Add Tenant
                    </button>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-6">
                    <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center">
                        <Users size={24} />
                    </div>
                    <div>
                        <div className="text-3xl font-bold text-navy-900 font-serif">{stats.totalTenants}</div>
                        <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">Total Tenants</div>
                    </div>
                </div>
                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-6">
                    <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center">
                        <TrendingUp size={24} />
                    </div>
                    <div>
                        <div className="text-3xl font-bold text-navy-900 font-serif">${stats.totalMrr.toLocaleString()}</div>
                        <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">Platform MRR</div>
                    </div>
                </div>
                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-6">
                    <div className="w-12 h-12 bg-red-50 text-red-600 rounded-xl flex items-center justify-center">
                        <AlertCircle size={24} />
                    </div>
                    <div>
                        <div className="text-3xl font-bold text-navy-900 font-serif">{stats.atRisk}</div>
                        <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">At Risk / Overdue</div>
                    </div>
                </div>
            </div>

            {/* Toolbar */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 mb-6 flex flex-col md:flex-row justify-between items-center gap-4">
                <div className="flex gap-2 bg-slate-100 p-1 rounded-lg">
                    {["All", "Broker", "SME", "Partner"].map((t) => (
                        <button
                            key={t}
                            onClick={() => setFilter(t.toLowerCase())}
                            className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${filter === t.toLowerCase() ? "bg-white text-navy-900 shadow-sm" : "text-slate-500 hover:text-navy-700"}`}
                        >
                            {t}
                        </button>
                    ))}
                </div>
                <div className="relative flex-1 md:w-80">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    <input
                        type="text"
                        placeholder="Search by name, email, or ID..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-navy-900/10"
                    />
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <table className="w-full text-left">
                    <thead className="text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-slate-50/30">
                        <tr>
                            <th className="px-6 py-4">Tenant</th>
                            <th className="px-6 py-4">Plan / MRR</th>
                            <th className="px-6 py-4">Users</th>
                            <th className="px-6 py-4">Features</th>
                            <th className="px-6 py-4">Health</th>
                            <th className="px-6 py-4">Joined</th>
                            <th className="px-6 py-4">Status</th>
                            <th className="px-6 py-4"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {loading ? (
                            <tr>
                                <td colSpan="8" className="px-6 py-8 text-center text-slate-500">Loading tenants...</td>
                            </tr>
                        ) : displayed.length === 0 ? (
                            <tr>
                                <td colSpan="8" className="px-6 py-8 text-center text-slate-500">No tenants found.</td>
                            </tr>
                        ) : (
                            displayed.map((row) => (
                                <React.Fragment key={row.id}>
                                    <tr className="hover:bg-slate-50 transition-colors group">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center font-bold text-slate-500 group-hover:bg-navy-900 group-hover:text-white transition-all flex-shrink-0">
                                                    {row.name ? row.name[0].toUpperCase() : "?"}
                                                </div>
                                                <div className="flex flex-col gap-0.5">
                                                    <span className="font-bold text-navy-900">{row.name || "Unnamed"}</span>
                                                    {row.contactEmail && (
                                                        <span className="text-[11px] text-slate-500">{row.contactEmail}</span>
                                                    )}
                                                    <div className="flex items-center gap-2">
                                                        {row.country && (
                                                            <span className="text-[10px] text-slate-400 flex items-center gap-0.5">
                                                                <Globe size={9} /> {row.country}
                                                            </span>
                                                        )}
                                                        <span className="text-[10px] text-slate-300 font-mono">{row.id.slice(0, 8)}…</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col">
                                                <span className="text-sm font-bold text-navy-900">{capitalize(row.paymentPlan) || "—"}</span>
                                                <span className="text-xs text-slate-400 font-mono">${(row.mrr || 0).toLocaleString()}/mo</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <button
                                                onClick={() => navigate(`${baseRoute}/users?tenant=${row.id}`)}
                                                className="flex items-center gap-1.5 text-sm font-bold text-navy-900 hover:text-blue-600 transition-colors"
                                                title="View users"
                                            >
                                                <Users size={14} className="text-slate-400" />
                                                {userCounts[row.id] || 0}
                                            </button>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-wrap gap-1 max-w-[200px]">
                                                {(row.enabledFeatures || []).length > 0 ? (
                                                    <>
                                                        {(row.enabledFeatures || []).slice(0, 3).map(feat => {
                                                            const mod = PLATFORM_MODULES.find(m => m.id === feat);
                                                            return (
                                                                <span key={feat} className="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded text-[9px] font-bold">
                                                                    {mod ? mod.label : feat}
                                                                </span>
                                                            );
                                                        })}
                                                        {(row.enabledFeatures || []).length > 3 && (
                                                            <span className="px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded text-[9px] font-bold">
                                                                +{row.enabledFeatures.length - 3}
                                                            </span>
                                                        )}
                                                    </>
                                                ) : (
                                                    <span className="text-[10px] text-slate-400 italic">None</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                <div className="w-16 bg-slate-100 h-1.5 rounded-full overflow-hidden">
                                                    <div
                                                        className={`h-full rounded-full ${row.healthScore > 80 ? "bg-emerald-500" : row.healthScore > 50 ? "bg-amber-500" : "bg-red-500"}`}
                                                        style={{ width: `${row.healthScore}%` }}
                                                    />
                                                </div>
                                                <span className="text-xs font-bold text-slate-600">{row.healthScore}%</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-slate-500">{formatDate(row.createdAt)}</td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${getStatusColor(row.status)}`}>
                                                {row.status || "unknown"}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end gap-1">
                                                <button
                                                    title="View Users"
                                                    onClick={() => navigate(`${baseRoute}/users?tenant=${row.id}`)}
                                                    className="p-2 hover:bg-navy-900 hover:text-white rounded-lg text-slate-400 transition-all"
                                                >
                                                    <LayoutDashboard size={15} />
                                                </button>
                                                <button
                                                    title="Edit Features"
                                                    onClick={() => openFeatureEditor(row)}
                                                    className="p-2 hover:bg-blue-600 hover:text-white rounded-lg text-slate-400 transition-all"
                                                >
                                                    <Settings size={15} />
                                                </button>
                                                {row.contactEmail && (
                                                    <a
                                                        href={`mailto:${row.contactEmail}`}
                                                        title="Email tenant"
                                                        className="p-2 hover:bg-navy-900 hover:text-white rounded-lg text-slate-400 transition-all"
                                                    >
                                                        <Mail size={15} />
                                                    </a>
                                                )}
                                                <button
                                                    title="Toggle feature details"
                                                    onClick={() => setExpandedRow(expandedRow === row.id ? null : row.id)}
                                                    className="p-2 hover:bg-slate-200 rounded-lg text-slate-400 transition-all"
                                                >
                                                    {expandedRow === row.id ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                    {expandedRow === row.id && (
                                        <tr className="bg-slate-50">
                                            <td colSpan="8" className="px-6 py-4">
                                                <div className="flex items-start gap-4">
                                                    <div className="flex-1">
                                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Enabled Modules</p>
                                                        <div className="flex flex-wrap gap-2">
                                                            {PLATFORM_MODULES.map(mod => {
                                                                const on = (row.enabledFeatures || []).includes(mod.id);
                                                                return (
                                                                    <span key={mod.id} className={`px-2.5 py-1 rounded-full text-[10px] font-bold border ${on ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-400 border-slate-200"}`}>
                                                                        {on ? "✓ " : ""}{mod.label}
                                                                    </span>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-col gap-2 flex-shrink-0">
                                                        <button
                                                            onClick={() => openFeatureEditor(row)}
                                                            className="px-4 py-2 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 flex items-center gap-2"
                                                        >
                                                            <Settings size={13} /> Edit Features
                                                        </button>
                                                        <button
                                                            onClick={() => sendPlanInvoice(row.id, row.name)}
                                                            disabled={invoicingSending === row.id}
                                                            className="px-4 py-2 bg-emerald-600 text-white text-xs font-bold rounded-lg hover:bg-emerald-700 flex items-center gap-2 disabled:opacity-50"
                                                        >
                                                            <CreditCard size={13} />
                                                            {invoicingSending === row.id ? "Sending…" : "Send Plan Invoice"}
                                                        </button>
                                                        <button
                                                            onClick={() => navigate(`${baseRoute}/sla`)}
                                                            className="px-4 py-2 bg-slate-700 text-white text-xs font-bold rounded-lg hover:bg-slate-800 flex items-center gap-2"
                                                        >
                                                            <FileText size={13} /> Create SLA
                                                        </button>
                                                    </div>
                                                    {invoiceFlash[row.id] && (
                                                        <div className={`mt-2 p-2 rounded-lg text-[11px] font-semibold col-span-2 ${invoiceFlash[row.id].ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                                                            {invoiceFlash[row.id].ok ? <CheckCircle size={12} className="inline mr-1"/> : <AlertCircle size={12} className="inline mr-1"/>}
                                                            {invoiceFlash[row.id].msg}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* ── Provision Modal ── */}
            {showProvision && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-900/40 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
                        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between rounded-t-2xl">
                            <div>
                                <h2 className="text-lg font-bold text-navy-900 flex items-center gap-2">
                                    <Building2 size={18} className="text-gold-500" /> Add New Tenant
                                </h2>
                                <p className="text-xs text-slate-500 mt-0.5">Creates a company record. Then add users from User Management.</p>
                            </div>
                            <button onClick={() => setShowProvision(false)} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-lg transition-colors">
                                <X size={18} />
                            </button>
                        </div>

                        <form onSubmit={handleProvision} className="p-6 space-y-4">
                            {provisionError && (
                                <p className="text-xs text-red-600 font-semibold bg-red-50 border border-red-100 rounded-lg px-3 py-2">{provisionError}</p>
                            )}

                            <div>
                                <label className="block text-xs font-bold text-slate-600 mb-1.5">Company Name *</label>
                                <input
                                    type="text"
                                    placeholder="e.g. Acme Logistics Pty Ltd"
                                    value={provisionForm.name}
                                    onChange={(e) => setProvisionForm(p => ({ ...p, name: e.target.value }))}
                                    className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-navy-900/10"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-600 mb-1.5">Contact Email *</label>
                                <input
                                    type="email"
                                    placeholder="admin@company.com"
                                    value={provisionForm.contactEmail}
                                    onChange={(e) => setProvisionForm(p => ({ ...p, contactEmail: e.target.value }))}
                                    className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-navy-900/10"
                                    required
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-1.5">Country</label>
                                    <input
                                        type="text"
                                        placeholder="AU"
                                        value={provisionForm.country}
                                        onChange={(e) => setProvisionForm(p => ({ ...p, country: e.target.value.toUpperCase() }))}
                                        maxLength={3}
                                        className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-navy-900/10"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-1.5">Plan</label>
                                    <select
                                        value={provisionForm.plan}
                                        onChange={(e) => setProvisionForm(p => ({ ...p, plan: e.target.value }))}
                                        className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-navy-900/10 bg-white"
                                    >
                                        {PLAN_OPTIONS.map(p => (
                                            <option key={p.value} value={p.value}>{p.label} — {p.priceRange}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
                                <p className="text-xs text-blue-700">
                                    Features will be pre-configured based on the selected plan. You can customize them after creation.
                                </p>
                            </div>

                            <div className="flex justify-end gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowProvision(false)}
                                    className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-bold hover:bg-slate-100 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={provisioning}
                                    className="px-6 py-2.5 rounded-xl bg-navy-900 text-white font-bold hover:bg-navy-800 transition-colors flex items-center gap-2 shadow-lg shadow-navy-900/20 disabled:opacity-50"
                                >
                                    {provisioning ? "Creating..." : <><Check size={16} /> Create Tenant</>}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ── Feature Edit Modal ── */}
            {editingTenant && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-900/40 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl flex flex-col max-h-[90vh] overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between shrink-0">
                            <div>
                                <h2 className="text-xl font-bold text-navy-900 font-serif flex items-center gap-2">
                                    <ShieldCheck size={20} className="text-gold-500" /> Edit Features
                                </h2>
                                <p className="text-sm text-slate-500 mt-0.5">
                                    Managing modules for <strong className="text-navy-900">{editingTenant.name}</strong>
                                </p>
                            </div>
                            <button onClick={() => setEditingTenant(null)} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-lg transition-colors">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto space-y-8 flex-1">
                            {Object.entries(groupedModules).map(([group, modules]) => (
                                <div key={group}>
                                    <h4 className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3 border-b border-slate-100 pb-2">
                                        {group}
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {modules.map((mod) => {
                                            const isEnabled = tempFeatures.includes(mod.id);
                                            return (
                                                <div
                                                    key={mod.id}
                                                    onClick={() => toggleFeature(mod.id)}
                                                    className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-all ${
                                                        isEnabled ? "border-emerald-500 bg-emerald-50/30 shadow-sm" : "border-slate-200 bg-white hover:border-slate-300"
                                                    }`}
                                                >
                                                    <span className={`text-sm font-bold ${isEnabled ? "text-emerald-900" : "text-slate-600"}`}>
                                                        {mod.label}
                                                    </span>
                                                    {isEnabled
                                                        ? <ToggleRight size={24} className="text-emerald-500" />
                                                        : <ToggleLeft size={24} className="text-slate-300" />
                                                    }
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex justify-end gap-3 shrink-0">
                            <button
                                onClick={() => setEditingTenant(null)}
                                className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-bold hover:bg-slate-100 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={saveFeatures}
                                disabled={savingFeatures}
                                className="px-6 py-2.5 rounded-xl bg-navy-900 text-white font-bold hover:bg-navy-800 transition-colors flex items-center gap-2 shadow-lg shadow-navy-900/20 disabled:opacity-50"
                            >
                                {savingFeatures ? "Saving..." : <><Check size={18} /> Save Features</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
